import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
  Button,
} from "@mui/material";
import { Timestamp } from "firebase/firestore";
import useFirestore from "../../../../config/fireBaseHook";
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";
import ConfirmDeleteDialog from "../../../common/ConfirmDeleteDialog";

const defaultFormData = {
  Credit_Capture_Status: "",
  Active: -1,
  id: "",
};

const Edit = ({
  open,
  onClose,
  creditCaptureStatusDetail,
  onRecordUpdated,
}) => {
  const { updateData, deleteData } = useFirestore("CreditCaptureStatus");
  const [formData, setFormData] = useState(
    creditCaptureStatusDetail || defaultFormData
  );
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setFormData(creditCaptureStatusDetail || defaultFormData);
    setErrors({});
  }, [creditCaptureStatusDetail]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.Credit_Capture_Status?.trim()) {
      newErrors.Credit_Capture_Status = "Credit Capture Status is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: name === "Active" ? parseInt(value) : value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      if (!formData.id) {
        throw new Error("Document ID is missing. Cannot update.");
      }

      const dataToUpdate = {
        ...formData,
      };

      await updateData(formData.id, dataToUpdate);
      setLoading(false);
      setSnackbarOpen(true);
      onRecordUpdated();
    } catch (error) {
      console.error("Error updating record:", error);
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      if (!formData.id) {
        throw new Error("Document ID is missing. Cannot delete.");
      }
      await deleteData(formData.id);
      setLoading(false);
      setSnackbarOpen(true);
      onRecordUpdated();
      onClose();
    } catch (error) {
      console.error("Error deleting record:", error);
      setLoading(false);
    }
  };

  const handleConfirmDeleteOpen = () => {
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDeleteClose = () => {
    setConfirmDeleteOpen(false);
  };

  const handleConfirmDelete = () => {
    handleConfirmDeleteClose();
    handleDelete();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  // The X (and backdrop/Escape, which share the same Dialog onClose) always
  // asks for confirmation first rather than closing immediately, so an
  // in-progress edit isn't lost by an accidental click.
  const handleCloseAttempt = () => {
    setConfirmCloseOpen(true);
  };

  const handleCancelClose = () => {
    setConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    setConfirmCloseOpen(false);
    handleClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleCloseAttempt} maxWidth="md" fullWidth>
        <ModalDialogTitle onClose={handleCloseAttempt}>
          Edit Credit Capture Status
        </ModalDialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Credit Capture Status"
                name="Credit_Capture_Status"
                value={formData.Credit_Capture_Status || ""}
                onChange={handleChange}
                fullWidth
                margin="normal"
                required
                error={!!errors.Credit_Capture_Status}
                helperText={errors.Credit_Capture_Status}
              />
              <FormControl fullWidth margin="normal" required>
                <InputLabel id="active-label">Active</InputLabel>
                <Select
                  labelId="active-label"
                  name="Active"
                  value={formData.Active ?? -1}
                  onChange={handleChange}
                  label="Active"
                >
                  <MenuItem value={-1}>Active</MenuItem>
                  <MenuItem value={0}>Inactive</MenuItem>
                </Select>
                <FormHelperText>
                  Select whether this credit capture status is active or
                  inactive
                </FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <ModalActions
          onSave={handleSubmit}
          saving={loading}
          onDelete={handleConfirmDeleteOpen}
        />
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          message="Record has been updated"
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          ContentProps={{
            sx: {
              backgroundColor: "black",
              color: "white",
            },
          }}
        />
      </Dialog>
      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onClose={handleConfirmDeleteClose}
        onConfirm={handleConfirmDelete}
        deleting={loading}
        message="Are you sure you want to delete this credit capture status record? This action cannot be undone."
      />
      <Dialog
        open={confirmCloseOpen}
        onClose={handleCancelClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            Are you sure you want to close before saving your changes?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelClose}>No</Button>
          <Button onClick={handleConfirmClose} variant="contained" color="error">
            Yes, Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Edit;
